import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { onUnauthorized } from "@/lib/api";
import { hereOf, loginPath } from "@/lib/loginPath";
import { onSignInPrompt } from "@/lib/signInPrompt";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Global replacement for raw "Not authenticated" toasts. The axios response interceptor in
 * lib/api.js calls the callback registered here whenever any request comes back 401, so any
 * screen that forgot to pre-guard an action (e.g. Cart's placeOrder) gets a friendly prompt
 * instead of the backend's raw error detail.
 */
export function SignInPrompt() {
  const { t, tenant } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Where to come back to. Set by `promptSignIn(next)`; the 401 interceptor passes nothing, so it
  // falls back to the page the person is on when they press Sign in.
  const [next, setNext] = useState("");

  useEffect(() => {
    onUnauthorized(() => {
      setNext("");
      setOpen(true);
    });
    onSignInPrompt((to) => {
      setNext(to || "");
      setOpen(true);
    });
    return () => {
      onUnauthorized(null);
      onSignInPrompt(null);
    };
  }, []);

  const goToLogin = () => {
    setOpen(false);
    navigate(loginPath(next || hereOf(location), tenant?.slug));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="sign-in-prompt">
        <DialogHeader>
          <DialogTitle>{t("common.signIn")}</DialogTitle>
          <DialogDescription>{t("errors.unauthorized")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" data-testid="sign-in-prompt-cancel" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button data-testid="sign-in-prompt-confirm" onClick={goToLogin}>
            {t("common.signIn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
